/**
 * World-renderer lifecycle — the single entry point the React shell invokes
 * when the world channel mounts.
 *
 * Responsibilities:
 *   - Build the PixiJS Application + layer hierarchy (`createSceneGraph`).
 *   - Wire input-driven camera state (`createCameraController`).
 *   - Instantiate the per-layer rendering subsystems (tiles, walls, props)
 *     that subscribe to the world model.
 *   - Drive a 60 fps ticker callback that:
 *       1. Applies the camera transform to the world container.
 *       2. Reads the current `WorldModel` from `useWorldModel` and hands
 *          it to each subsystem's `render(model, cam, w, h)`.
 *
 * The ticker reads from the zustand store out-of-React, so model updates
 * don't trigger React re-renders of the world pane.
 */

import { Graphics, type Application } from 'pixi.js';

import { deriveInspection, hitTestTile, useInspection } from '../inspect';
import { routeTileClick } from '../input/movement';
import { phase0, debugLog } from '../log';
import { useLiveWire } from '../wire';
import { screenToWorld, tileToIso, TILE_HEIGHT_PX, TILE_WIDTH_PX } from './iso';
import { useWorldModel } from '../world/store';
import { createAvatarRenderer, type AvatarRenderer } from './avatars';
import { createBubbleRenderer, type BubbleRenderer } from './bubbles';
import { createCameraController, type CameraController } from './camera';
import { createDayNightTinter, type DayNightTinter } from './daynight';
import { createFogRenderer, type FogRenderer } from './fog';
import { createSceneGraph, type SceneGraph } from './scene';
import { createPropRenderer, type PropRenderer } from './props';
import { createCliffEdgeRenderer, type CliffEdgeRenderer } from './cliff-edges';
import { createTileRenderer, type TileRenderer } from './tiles';
import { createWallRenderer, type WallRenderer } from './walls';
import { createWeatherRenderer, type WeatherRenderer } from './weather';

/** GH#8 D3 — green diamond outline that fades over ~250ms on the
 *  target tile, acknowledging a successful click-to-move dispatch. */
const MOVE_FLASH_MS = 250;
const MOVE_FLASH_COLOR = 0xb6f562;

export interface WorldRendererHandle {
  app: Application;
  scene: SceneGraph;
  camera: CameraController;
  /** Idempotent teardown. Safe to call from React StrictMode's unmount leg. */
  destroy: () => void;
}

export interface WorldRendererOptions {
  background?: string;
  antialias?: boolean;
  initialZoomIndex?: number;
}

export async function mountWorldRenderer(
  host: HTMLElement,
  options: WorldRendererOptions = {},
): Promise<WorldRendererHandle> {
  const scene = await createSceneGraph(host, {
    background: options.background,
    antialias: options.antialias,
  });

  // Expose the canvas to keyboard focus so arrow-key panning works without
  // clicking into the element first.
  scene.app.canvas.tabIndex = 0;

  const camera = createCameraController({
    target: scene.app.canvas,
    initialZoomIndex: options.initialZoomIndex,
  });

  // Layer renderers — each subscribes to the world model via `getState()`.
  const tiles: TileRenderer = createTileRenderer(scene.layers);
  const cliffEdges: CliffEdgeRenderer = createCliffEdgeRenderer(scene.layers);
  const walls: WallRenderer = createWallRenderer(scene.layers);
  const props: PropRenderer = createPropRenderer(scene.layers);
  const avatars: AvatarRenderer = createAvatarRenderer(scene.layers);
  const weather: WeatherRenderer = createWeatherRenderer(scene.layers);
  const tinter: DayNightTinter = createDayNightTinter(scene.worldContainer);
  const bubbles: BubbleRenderer = createBubbleRenderer(scene.layers);
  const fog: FogRenderer = createFogRenderer(scene.layers);

  // Click router — left-button clicks hit-test the tile under the
  // pointer and route to either move (cardinal-adjacent) or inspect
  // (everything else, plus shift-modifier override). See
  // `client/src/input/movement.ts` for the routing rules; this layer
  // only owns coord derivation and side-effect dispatch.
  // Right-click + middle-click stay reserved for camera pan / context.
  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return; // left-button only
    const rect = scene.app.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(
      sx,
      sy,
      camera.state,
      scene.app.screen.width,
      scene.app.screen.height,
    );
    const tileX = Math.floor(x);
    const tileY = Math.floor(y);

    const wm = useWorldModel.getState();
    const selfPlayer =
      wm.selfPlayerId !== null ? wm.model.players.get(wm.selfPlayerId) : null;
    const action = routeTileClick({
      selfTile: selfPlayer ? selfPlayer.position : null,
      clickTile: [tileX, tileY],
      shiftKey: e.shiftKey,
    });

    if (action.kind === 'move') {
      const sent = useLiveWire.getState().send({
        plane: 'World',
        intent: { Move: { direction: action.direction } },
      });
      phase0.info('move_intent_dispatch', {
        direction: action.direction,
        tile_x: tileX,
        tile_y: tileY,
        sent,
      });
      debugLog('input', 'info', `move direction=${action.direction} tile=(${tileX},${tileY}) sent=${sent}`);
      // Acknowledge the click visually only when the intent reached the
      // wire — throttled sends already raise their own toast via the
      // live-wire layer. No optimistic position update per I6; the
      // avatar re-renders on the resulting `PlayerMoved` event.
      if (sent) flashTargetTile(scene, tileX, tileY);
      return;
    }

    // action.kind === 'inspect'
    const target = hitTestTile(wm.model, tileX, tileY);
    const data = deriveInspection(wm.model, target);
    if (data) {
      useInspection.getState().openWith(target, data);
      const kind = 'Tile' in target ? 'Tile' : 'Prop' in target ? 'Prop' : 'Building' in target ? 'Building' : 'Player' in target ? 'Player' : 'Soma';
      phase0.info('inspect_request', { tile_x: tileX, tile_y: tileY, kind });
      debugLog('input', 'info', `inspect tile=(${tileX},${tileY}) kind=${kind}`);
    }
  };
  scene.app.canvas.addEventListener('pointerdown', onPointerDown);

  /** GH#8 follow-up: one-shot camera centering on the local player as
   *  soon as the model carries a known position for them. Without this,
   *  the camera defaults to world (0, 0) but the spawn lands at the
   *  arrival-zone centre (often hundreds of tiles away on a 512×512
   *  world), so the operator never sees the avatar and click-to-move
   *  has nothing to aim at. Per CLIENT_DESIGN §5 we still don't follow
   *  the avatar after this initial centring — the operator pans freely
   *  thereafter. */
  let centeredOnSelf = false;

  const tickerFn = (): void => {
    const viewW = scene.app.screen.width;
    const viewH = scene.app.screen.height;
    camera.apply(scene.worldContainer, viewW, viewH);

    const state = useWorldModel.getState();
    const { model, selfPlayerId, tickHz, snapshotApplied } = state;
    const now = performance.now();

    // Wait for `snapshotApplied` so we centre on the spawn coords from
    // the wire, not the `[0, 0]` placeholder the GH#7 C2 pre-seed
    // installs on `HandshakeAck` (which lands ~250 ms before the
    // snapshot in a Fresh-mode connect).
    if (!centeredOnSelf && snapshotApplied && selfPlayerId !== null) {
      const self = model.players.get(selfPlayerId);
      if (self) {
        const iso = tileToIso(self.position[0], self.position[1]);
        camera.state.panX = iso.x;
        camera.state.panY = iso.y;
        centeredOnSelf = true;
        phase0.info('camera_centered_on_self', {
          player_id: selfPlayerId,
          tile_x: self.position[0],
          tile_y: self.position[1],
        });
        debugLog('input', 'info', `camera_centered player=${selfPlayerId} tile=(${self.position[0]},${self.position[1]})`);
      }
    }

    // Cutaway check per §4.5: only the building the local player is in
    // cuts away. `-1` sentinel = no cutaway active.
    let cutawayRoomId = -1;
    if (selfPlayerId !== null) {
      const self = model.players.get(selfPlayerId);
      if (self && model.width > 0) {
        const [px, py] = self.position;
        if (px >= 0 && px < model.width && py >= 0 && py < model.height) {
          const rid = model.tileRoomIds[py * model.width + px] ?? -1;
          if (rid !== undefined && rid >= 0) cutawayRoomId = rid;
        }
      }
    }

    tiles.render(model, camera.state, viewW, viewH);
    cliffEdges.render(model, camera.state, viewW, viewH);
    walls.render(model, camera.state, viewW, viewH, cutawayRoomId);
    props.render(model, camera.state, viewW, viewH);
    avatars.render(model, camera.state, selfPlayerId, tickHz, now);
    fog.render(model, camera.state, selfPlayerId, viewW, viewH);

    // Day/night tint: indoor palette if the local player is in a room;
    // otherwise outdoor tint from phase + day_tick (M2.1).
    tinter.apply(model.clock.phase, model.clock.day_tick, cutawayRoomId >= 0);

    // Weather is screen-space — pass current viewport dimensions directly.
    weather.render(model.weather, viewW, viewH);

    // Speech bubbles ride on the overlay (screen space) but compute
    // their screen position from the speaker's world-space tile each
    // frame so the bubble tracks the avatar as the camera pans.
    bubbles.render(model, camera.state, viewW, viewH, now);
  };
  scene.app.ticker.add(tickerFn);

  /** Brief diamond-outline flash on the click target — fades alpha
   *  over `MOVE_FLASH_MS` via the existing PixiJS ticker, then self-
   *  destroys. Lives on the world-space floor-overlay layer so it
   *  pans + zooms with the tile. */
  function flashTargetTile(s: SceneGraph, tileX: number, tileY: number): void {
    const halfW = TILE_WIDTH_PX / 2;
    const halfH = TILE_HEIGHT_PX / 2;
    const { x: ix, y: iy } = tileToIso(tileX, tileY);
    const g = new Graphics();
    g.poly([ix, iy - halfH, ix + halfW, iy, ix, iy + halfH, ix - halfW, iy]);
    g.stroke({ color: MOVE_FLASH_COLOR, width: 3, alpha: 0.95 });
    g.fill({ color: MOVE_FLASH_COLOR, alpha: 0.22 });
    s.layers.floorOverlayLayer.addChild(g);

    const startedAt = performance.now();
    const fade = (): void => {
      const t = (performance.now() - startedAt) / MOVE_FLASH_MS;
      if (t >= 1) {
        s.app.ticker.remove(fade);
        g.destroy();
        return;
      }
      g.alpha = 1 - t;
    };
    s.app.ticker.add(fade);
  }

  let destroyed = false;
  return {
    app: scene.app,
    scene,
    camera,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      scene.app.canvas.removeEventListener('pointerdown', onPointerDown);
      scene.app.ticker.remove(tickerFn);
      tiles.destroy();
      cliffEdges.destroy();
      walls.destroy();
      props.destroy();
      avatars.destroy();
      weather.destroy();
      tinter.destroy();
      bubbles.destroy();
      fog.destroy();
      camera.destroy();
      scene.destroy();
    },
  };
}

